import { PaymentRecord, BillData, Company } from '../types/billing';

export interface BankTransaction {
  date: string;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
  balance: number;
  reference?: string;
}

export interface PaymentMatch {
  transaction: BankTransaction;
  bill?: BillData;
  company?: Company;
  confidence: number; // 0-1, how confident we are in the match
  matchReason: string;
}

export class PaymentProcessor {
  private companies: Company[];
  private bills: BillData[];

  constructor(companies: Company[], bills: BillData[]) {
    this.companies = companies;
    this.bills = bills;
  }

  /**
   * Clean company name by removing prefixes, suffixes, and common business terms
   */
  private cleanCompanyName(companyName: string): string {
    return companyName
      .toLowerCase()
      .trim()
      // Remove M/S. or M/S prefix
      .replace(/^m\/s\.?\s*/i, '')
      .replace(/^m\/s\s*/i, '')
      // Remove common business suffixes
      .replace(/\b(pvt|private)\s*(ltd|limited)?\.?\s*$/gi, '')
      .replace(/\b(ltd|limited)\.?\s*$/gi, '')
      .replace(/\b(inc|incorporated)\.?\s*$/gi, '')
      .replace(/\b(llp|llc)\.?\s*$/gi, '')
      .replace(/\b(co|company)\.?\s*$/gi, '')
      .trim();
  }

  /**
   * Check if a word in company name matches a word in description (handles abbreviations)
   */
  private wordMatches(companyWord: string, description: string): boolean {
    const lowerWord = companyWord.toLowerCase();
    const lowerDesc = description.toLowerCase();
    
    // Exact match
    if (lowerDesc.includes(lowerWord)) {
      return true;
    }
    
    // Check if company word starts with description word (for abbreviations like "BOILE" matching "BOILERS")
    // Or if description word starts with company word
    const descWords = lowerDesc.split(/[\s\-.,/]+/);
    for (const descWord of descWords) {
      // If either word is a prefix of the other (minimum 4 chars to avoid false matches)
      if (lowerWord.length >= 4 && descWord.length >= 4) {
        if (lowerWord.startsWith(descWord) || descWord.startsWith(lowerWord)) {
          return true;
        }
      }
      // For shorter words, require exact match
      if (lowerWord.length < 4 && descWord === lowerWord) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Check if a transaction matches any company by NAME ONLY
   * Only matches if the company name appears in the transaction description
   * Handles M/S. prefixes, abbreviations, and common suffixes
   * Example: "PRIVILEGE BIKSONS BOILE" matches "M/S. PRIVILEGE BIKSONS BOILERS PVT. LTD."
   */
  private matchesAnyCompany(transaction: BankTransaction): { matched: boolean; matchedCompany?: Company; reason?: string } {
    const description = transaction.description.toLowerCase();
    
    for (const company of this.companies) {
      const originalCompanyName = company.name;
      const companyName = company.name.toLowerCase().trim();
      
      // Skip if company name is empty
      if (!companyName) continue;
      
      // Clean the company name (remove M/S., PVT LTD, etc.)
      const cleanCompanyName = this.cleanCompanyName(companyName);
      
      // Check if cleaned company name appears in description
      if (description.includes(cleanCompanyName)) {
        console.log(`✓ Exact match: "${originalCompanyName}" (cleaned: "${cleanCompanyName}") found in "${transaction.description}"`);
        return { matched: true, matchedCompany: company, reason: `Exact match: ${originalCompanyName}` };
      }
      
      // Split cleaned company name into words
      const companyWords = cleanCompanyName
        .split(/[\s\-.,/]+/)
        .filter(word => word.length > 2); // Words longer than 2 characters
      
      if (companyWords.length > 0) {
        // Check if significant words from company name appear in description
        const matchedWords = companyWords.filter(word => this.wordMatches(word, description));
        
        // If at least 60% of words match, consider it a match
        const matchRatio = matchedWords.length / companyWords.length;
        if (matchRatio >= 0.6 && matchedWords.length >= 2) {
          console.log(`✓ Partial match: "${originalCompanyName}" (${matchedWords.length}/${companyWords.length} words: ${matchedWords.join(', ')}) in "${transaction.description}"`);
          return { 
            matched: true, 
            matchedCompany: company, 
            reason: `Partial match: ${matchedWords.length}/${companyWords.length} words (${matchedWords.join(', ')}) from ${originalCompanyName}` 
          };
        }
        
        // Special case: if we have at least 2 matching words and they're significant
        if (matchedWords.length >= 2) {
          const significantWords = matchedWords.filter(w => w.length >= 4);
          if (significantWords.length >= 2) {
            console.log(`✓ Significant words match: "${originalCompanyName}" (${significantWords.join(', ')}) in "${transaction.description}"`);
            return { 
              matched: true, 
              matchedCompany: company, 
              reason: `Significant words match: ${significantWords.join(', ')} from ${originalCompanyName}` 
            };
          }
        }
      }
    }
    
    return { matched: false };
  }

  /**
   * Process bank transactions and create payment records
   * Only processes CREDIT transactions where the company NAME appears in the description
   * Example: "NEFT/IBKL251112986736/BTU TECHNOLOGIES INDIA" will only process if "BTU TECHNOLOGIES INDIA" is a company name
   */
  public processTransactions(transactions: BankTransaction[]): PaymentRecord[] {
    // Filter only credit transactions (money received)
    const creditTransactions = transactions.filter(t => t.type === 'credit');
    const payments: PaymentRecord[] = [];
    
    console.log(`Processing ${creditTransactions.length} credit transactions...`);
    console.log(`Available companies: ${this.companies.map(c => c.name).join(', ')}`);

    for (const transaction of creditTransactions) {
      console.log(`\nChecking transaction: "${transaction.description}" (Amount: ₹${transaction.amount})`);
      
      // CRITICAL FILTER: Only process if company NAME appears in transaction description
      const companyMatch = this.matchesAnyCompany(transaction);
      
      if (!companyMatch.matched) {
        console.log(`✗ No match found for: "${transaction.description}"`);
        console.log(`  Available company names: ${this.companies.map(c => c.name).join(', ')}`);
        continue; // Skip transactions that don't match any company name
      }
      
      console.log(`✓ Company matched: ${companyMatch.matchedCompany?.name} - ${companyMatch.reason}`);
      
      // Find the best matching company and bill
      const match = this.findBestMatch(transaction);
      
      // Only process if we have a valid match with a company
      // The company name must be present (already checked above)
      if (match && match.company) {
        const payment: PaymentRecord = {
          id: '', // Will be generated by Firebase
          companyId: match.company.id,
          billId: match.bill?.id,
          amount: transaction.amount,
          date: transaction.date,
          mode: this.determinePaymentMode(transaction.description),
          reference: transaction.reference || this.extractReference(transaction.description),
          notes: `Auto-processed: ${match.matchReason} | ${transaction.description}`
        };
        
        console.log(`✓ Payment created for ${match.company.name}: ₹${transaction.amount}`);
        payments.push(payment);
      } else {
        console.log(`✗ No valid match found for transaction: "${transaction.description}"`);
      }
    }

    console.log(`\nTotal payments processed: ${payments.length} out of ${creditTransactions.length} credit transactions`);
    return payments;
  }

  /**
   * Find the best match for a transaction
   * Only matches if company NAME appears in the description (no GST matching)
   * Uses cleaned company names (removes M/S., PVT LTD, etc.)
   */
  private findBestMatch(transaction: BankTransaction): PaymentMatch | null {
    const matches: PaymentMatch[] = [];
    const description = transaction.description.toLowerCase();

    // First, try to match with companies - ONLY by company name
    for (const company of this.companies) {
      const companyName = company.name.toLowerCase().trim();
      const cleanCompanyName = this.cleanCompanyName(companyName);
      
      // CRITICAL: Only match if cleaned company NAME appears in description
      // Also check if any significant words match
      const companyWords = cleanCompanyName.split(/[\s\-.,/]+/).filter(w => w.length > 2);
      const hasMatch = description.includes(cleanCompanyName) || 
                       companyWords.some(word => this.wordMatches(word, description));
      
      if (hasMatch) {
        const confidence = this.calculateCompanyMatchConfidence(transaction, company);
        if (confidence > 0) {
          matches.push({
            transaction,
            company,
            confidence,
            matchReason: this.getCompanyMatchReason(transaction, company)
          });
        }
      }
    }

    // Then try to match with bills (but only if company name appears in transaction)
    // This helps link payments to specific invoices
    for (const bill of this.bills) {
      const billCompany = this.companies.find(c => c.id === bill.clientId);
      if (billCompany) {
        const companyName = billCompany.name.toLowerCase().trim();
        const cleanCompanyName = this.cleanCompanyName(companyName);
        const companyWords = cleanCompanyName.split(/[\s\-.,/]+/).filter(w => w.length > 2);
        
        // Only match bills if the cleaned company NAME appears in the transaction description
        const hasMatch = description.includes(cleanCompanyName) || 
                         companyWords.some(word => this.wordMatches(word, description));
        
        if (hasMatch) {
          const confidence = this.calculateBillMatchConfidence(transaction, bill);
          if (confidence > 0) {
            matches.push({
              transaction,
              bill,
              company: billCompany,
              confidence,
              matchReason: this.getBillMatchReason(transaction, bill)
            });
          }
        }
      }
    }

    // Return the best match (prefer matches with both company and bill)
    if (matches.length === 0) return null;
    
    // Prioritize matches that have both company and bill
    const matchesWithBill = matches.filter(m => m.bill && m.company);
    if (matchesWithBill.length > 0) {
      return matchesWithBill.reduce((best, current) => 
        current.confidence > best.confidence ? current : best
      );
    }
    
    // Otherwise return best company match
    return matches.reduce((best, current) => 
      current.confidence > best.confidence ? current : best
    );
  }

  /**
   * Round amount to whole number (same as bill PDF - >= 0.50 rounds up, < 0.50 rounds down)
   */
  private roundAmount(amount: number): number {
    return Math.round(amount);
  }

  /**
   * Calculate confidence score for bill matching
   */
  private calculateBillMatchConfidence(transaction: BankTransaction, bill: BillData): number {
    let confidence = 0;

    // Round both amounts to 2 decimal places for accurate comparison
    const transactionAmount = this.roundAmount(transaction.amount);
    const billAmount = this.roundAmount(bill.totalAmount);
    const amountDiff = Math.abs(transactionAmount - billAmount);
    const amountTolerance = billAmount * 0.05; // 5% tolerance
    const epsilon = 0.01; // Small tolerance for floating-point comparison (1 paisa)
    
    // Exact match (within 1 paisa tolerance)
    if (amountDiff <= epsilon) {
      confidence += 0.4; // Exact amount match
    } else if (amountDiff <= amountTolerance) {
      confidence += 0.3; // Close amount match
    } else if (transactionAmount <= billAmount) {
      confidence += 0.2; // Partial payment
    }

    // Description matching
    const description = transaction.description.toLowerCase();
    const clientName = bill.clientName.toLowerCase();
    
    if (description.includes(clientName)) {
      confidence += 0.3;
    }

    // Reference matching
    if (transaction.reference && bill.invoiceNumber) {
      if (description.includes(bill.invoiceNumber.toLowerCase())) {
        confidence += 0.2;
      }
    }

    // Date proximity (recent bills get higher score)
    const transactionDate = new Date(transaction.date);
    const billDate = new Date(bill.invoiceDate);
    const daysDiff = Math.abs(transactionDate.getTime() - billDate.getTime()) / (1000 * 60 * 60 * 24);
    
    if (daysDiff <= 30) {
      confidence += 0.1;
    }

    return Math.min(confidence, 1); // Cap at 1
  }

  /**
   * Calculate confidence score for company matching
   * Only based on company NAME (no GST matching)
   */
  private calculateCompanyMatchConfidence(transaction: BankTransaction, company: Company): number {
    let confidence = 0;
    const description = transaction.description.toLowerCase();
    const companyName = company.name.toLowerCase().trim();

    // Company name matching - exact match gets highest score
    if (description.includes(companyName)) {
      confidence += 1.0; // Full confidence for exact name match
    } else {
      // Partial name matching - split and check words
      const companyWords = companyName
        .replace(/\b(ltd|limited|pvt|private|inc|incorporated|llp|llc|co|company)\b/gi, '')
        .trim()
        .split(/[\s\-.,/]+/)
        .filter(word => word.length > 2);
      
      if (companyWords.length > 0) {
        const matchedWords = companyWords.filter(word => description.includes(word.toLowerCase()));
        
        // Calculate confidence based on percentage of words matched
        if (matchedWords.length > 0) {
          confidence += (matchedWords.length / companyWords.length) * 0.7;
        }
      }
    }

    return Math.min(confidence, 1);
  }

  /**
   * Get reason for bill match
   */
  private getBillMatchReason(transaction: BankTransaction, bill: BillData): string {
    const reasons: string[] = [];
    
    // Round both amounts to 2 decimal places for accurate comparison
    const transactionAmount = this.roundAmount(transaction.amount);
    const billAmount = this.roundAmount(bill.totalAmount);
    const amountDiff = Math.abs(transactionAmount - billAmount);
    const epsilon = 0.01; // Small tolerance for floating-point comparison (1 paisa)
    
    if (amountDiff <= epsilon) {
      reasons.push('Exact amount match');
    } else if (amountDiff <= billAmount * 0.05) {
      reasons.push('Close amount match');
    }
    
    if (transaction.description.toLowerCase().includes(bill.clientName.toLowerCase())) {
      reasons.push('Client name in description');
    }
    
    if (transaction.reference && bill.invoiceNumber && 
        transaction.description.toLowerCase().includes(bill.invoiceNumber.toLowerCase())) {
      reasons.push('Invoice number match');
    }

    return reasons.join(', ') || 'General match';
  }

  /**
   * Get reason for company match
   * Only based on company name (no GST)
   */
  private getCompanyMatchReason(transaction: BankTransaction, company: Company): string {
    const reasons: string[] = [];
    const description = transaction.description.toLowerCase();
    const companyName = company.name.toLowerCase();
    
    if (description.includes(companyName)) {
      reasons.push('Company name match');
    } else {
      // Check for partial match
      const companyWords = companyName
        .replace(/\b(ltd|limited|pvt|private|inc|incorporated|llp|llc|co|company)\b/gi, '')
        .trim()
        .split(/[\s\-.,/]+/)
        .filter(word => word.length > 2);
      
      const matchedWords = companyWords.filter(word => description.includes(word.toLowerCase()));
      if (matchedWords.length > 0) {
        reasons.push(`Partial company name match (${matchedWords.length}/${companyWords.length} words)`);
      }
    }

    return reasons.join(', ') || 'Company name match';
  }

  /**
   * Determine payment mode from description
   */
  private determinePaymentMode(description: string): PaymentRecord['mode'] {
    const lowerDesc = description.toLowerCase();
    
    if (lowerDesc.includes('neft')) return 'NEFT';
    if (lowerDesc.includes('imps')) return 'IMPS';
    if (lowerDesc.includes('rtgs')) return 'RTGS';
    if (lowerDesc.includes('upi')) return 'UPI';
    if (lowerDesc.includes('cheque') || lowerDesc.includes('chq')) return 'Cheque';
    if (lowerDesc.includes('cash')) return 'Cash';
    
    return 'NEFT'; // Default
  }

  /**
   * Extract reference number from description
   */
  private extractReference(description: string): string {
    // Common patterns for reference numbers
    const patterns = [
      /(?:ref|refno|ref no|transaction|txn|utr)[\s:]*([a-zA-Z0-9]+)/i,
      /([A-Z0-9]{10,})/g, // UTR-like patterns
      /(\d{10,})/g // Long numeric patterns
    ];

    for (const pattern of patterns) {
      const match = description.match(pattern);
      if (match) {
        return match[1] || match[0];
      }
    }

    return '';
  }

  /**
   * Get payment summary statistics
   */
  public getPaymentSummary(payments: PaymentRecord[]): {
    totalAmount: number;
    totalCount: number;
    byMode: Record<string, { count: number; amount: number }>;
    byCompany: Record<string, { count: number; amount: number }>;
  } {
    const summary = {
      totalAmount: 0,
      totalCount: payments.length,
      byMode: {} as Record<string, { count: number; amount: number }>,
      byCompany: {} as Record<string, { count: number; amount: number }>
    };

    for (const payment of payments) {
      summary.totalAmount += payment.amount;

      // By mode
      if (!summary.byMode[payment.mode]) {
        summary.byMode[payment.mode] = { count: 0, amount: 0 };
      }
      summary.byMode[payment.mode].count++;
      summary.byMode[payment.mode].amount += payment.amount;

      // By company
      const company = this.companies.find(c => c.id === payment.companyId);
      const companyName = company?.name || 'Unknown';
      if (!summary.byCompany[companyName]) {
        summary.byCompany[companyName] = { count: 0, amount: 0 };
      }
      summary.byCompany[companyName].count++;
      summary.byCompany[companyName].amount += payment.amount;
    }

    return summary;
  }
}
